import daMessages from "../src/locales/da.json";
import deMessages from "../src/locales/de.json";
import enMessages from "../src/locales/en.json";
import fiMessages from "../src/locales/fi.json";
import nlMessages from "../src/locales/nl.json";
import noMessages from "../src/locales/no.json";
import svMessages from "../src/locales/sv.json";

export const LANGUAGES = ["da", "de", "en", "fi", "nl", "no", "sv"] as const;
export type TLanguage = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: TLanguage = "en";

export const LOCALE_MAP = {
  da: "da-DK",
  de: "de-DE",
  en: "en-US",
  fi: "fi-FI",
  nl: "nl-NL",
  no: "nb-NO",
  sv: "sv-SE",
} as const satisfies Record<TLanguage, string>;

export type TLocale = (typeof LOCALE_MAP)[keyof typeof LOCALE_MAP];

export const LANGUAGE_NAMES = {
  da: "Dansk",
  de: "Deutsch",
  en: "English",
  fi: "Suomi",
  nl: "Nederlands",
  no: "Norsk",
  sv: "Svenska",
} as const satisfies Record<TLanguage, string>;

export const MESSAGES_MAP: Record<TLanguage, Record<string, string>> = {
  de: deMessages,
  da: daMessages,
  en: enMessages,
  fi: fiMessages,
  no: noMessages,
  nl: nlMessages,
  sv: svMessages,
};

export const I18N_PATHS = {
  source: "src/**/*.{js,jsx,ts,tsx}",
  ignore: ["src/**/*.d.ts", "**/node_modules/**"],
  localesDir: "src/locales",
};

export const getLocaleFileName = (lang: TLanguage): string => `${lang}.json`;

export const isValidLanguage = (lang: unknown): lang is TLanguage =>
  // oxlint-disable-next-line typescript/consistent-type-assertions
  LANGUAGES.includes(lang as TLanguage);

export const extractLanguageFromPath = (pathname: string): TLanguage => {
  const parts = pathname.split("/").filter(Boolean);
  const maybeLang = parts[0];

  return isValidLanguage(maybeLang) ? maybeLang : DEFAULT_LANGUAGE;
};

export const stripLanguageFromPath = (pathname: string): string => {
  const parts = pathname.split("/").filter(Boolean);

  if (isValidLanguage(parts[0])) {
    return `/${parts.slice(1).join("/")}`;
  }

  return pathname;
};

export const buildLocalizedPath = (lang: TLanguage, path: string): string => {
  if (lang === DEFAULT_LANGUAGE) {
    return path === "/" ? "/" : path;
  }

  const cleanPath = path === "/" ? "" : path;

  return `/${lang}${cleanPath}`;
};

export const getGatsbyI18nConfig = () => ({
  defaultLocale: LOCALE_MAP[DEFAULT_LANGUAGE],
  locales: LANGUAGES.map(lang => ({
    locale: LOCALE_MAP[lang],
    prefix: lang,
    messages: MESSAGES_MAP[lang],
    slugs: {},
  })),
});

// eslint-disable-next-line import/no-default-export
export default {
  LANGUAGES,
  DEFAULT_LANGUAGE,
  LOCALE_MAP,
  LANGUAGE_NAMES,
  MESSAGES_MAP,
  I18N_PATHS,
  getLocaleFileName,
  isValidLanguage,
  extractLanguageFromPath,
  stripLanguageFromPath,
  buildLocalizedPath,
  getGatsbyI18nConfig,
};
